import React, { useState, useEffect, useCallback } from 'react';
import { getLegacyApiUrl } from '../utils/apiUtils';
import StudentSidebar from '../components/StudentSidebar';
import { useAuth } from '../contexts/AuthContext';

const StudentProfile = () => {
  const { currentUser } = useAuth();
  const [studentInfo, setStudentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchStudentInfo = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      console.log('Current user:', currentUser);
      console.log('User role:', currentUser?.role);
      
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      // Use environment variable for API URL
      const apiUrl = getLegacyApiUrl();
      console.log('Fetching from:', `${apiUrl}/students/profile`);
      console.log('Token:', token ? 'Present' : 'Missing');
      
      const response = await fetch(`${apiUrl}/students/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.log('Error response:', errorText);
        throw new Error(`Failed to fetch profile (${response.status}): ${errorText}`);
      }

      const data = await response.json();
      console.log('Profile data received:', data);
      setStudentInfo(data.student);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError(error.message || 'Failed to load profile information');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchStudentInfo();
  }, [fetchStudentInfo]);

  const getInitials = (name) => {
    if (!name) return 'S';
    return name.charAt(0).toUpperCase();
  };

  if (loading) {
    return (
      <div style={{display: 'flex', minHeight: '100vh', background: '#F5F6FA'}}>
        <StudentSidebar />
        <div style={{ 
          marginLeft: 290, 
          flex: 1, 
          padding: '30px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            fontSize: 18,
            color: '#64748B',
            fontFamily: 'Outfit'
          }}>
            Loading profile...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{display: 'flex', minHeight: '100vh', background: '#F5F6FA'}}>
        <StudentSidebar />
        <div style={{ 
          marginLeft: 290, 
          flex: 1, 
          padding: '30px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            fontSize: 18,
            color: '#EF4444',
            fontFamily: 'Outfit'
          }}>
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{display: 'flex', minHeight: '100vh', background: '#F5F6FA'}}>
      <StudentSidebar />
      
      <div style={{marginLeft: 290, width: 'calc(100% - 290px)', minHeight: '100vh', background: '#F5F6FA', position: 'relative'}}>
        {/* Page Title */}
        <div style={{padding: '32px 40px 20px 40px'}}>
          <h1 style={{fontSize: 36, fontFamily: 'Outfit, sans-serif', fontWeight: '700', color: '#131A1A', margin: 0}}>
            Profile
          </h1>
          <p style={{fontSize: 16, color: '#798AA3', margin: '8px 0 0 0', fontFamily: 'Outfit, sans-serif'}}>
            View your personal information and account details
          </p>
        </div>

        <div style={{width: 800, margin: '0 auto', padding: '0 40px'}}>
          <div style={{position: 'relative', marginBottom: '80px'}}>
            {/* Cover Image */}
            <div style={{width: '100%', height: 160}}>
              <img 
                src="/assets/Content.png" 
                alt="Cover" 
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: 16,
                  border: '1px solid #A8B9CA',
                  objectFit: 'cover'
                }}
              />
            </div>
            
            {/* Profile Image */}
            <div style={{
              position: 'absolute',
              bottom: -48,
              left: '50%',
              transform: 'translateX(-50%)'
            }}>
              <div 
                style={{
                  width: 96,
                  height: 96,
                  borderRadius: '50%',
                  border: '3px solid #FBFCFE',
                  background: '#F04915',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 36,
                  fontWeight: '700',
                  color: 'white',
                  fontFamily: 'Outfit, sans-serif'
                }}
              >
                {getInitials(studentInfo?.full_name || studentInfo?.name)}
              </div>
            </div>
          </div>

          {/* Profile Info */}
          <div style={{textAlign: 'center', marginBottom: '40px'}}>
            <h2 style={{color: '#F04915', fontSize: 24, fontFamily: 'Outfit, sans-serif', fontWeight: '600', marginBottom: '8px'}}>
              {studentInfo?.full_name || studentInfo?.name || 'Student Name'}
            </h2>
            <div style={{color: '#798AA3', fontSize: 16, fontFamily: 'Outfit, sans-serif', fontWeight: '400', marginBottom: '4px'}}>
              @{studentInfo?.email?.split('@')[0] || 'username'}
            </div>
            <div style={{color: '#131A1A', fontSize: 18, fontFamily: 'Outfit, sans-serif', fontWeight: '500', textTransform: 'capitalize'}}>
              Student
            </div>
          </div>

          {/* ID Section */}
          <div style={{textAlign: 'center', marginBottom: '40px'}}>
            <div style={{fontSize: 28, fontFamily: 'Outfit, sans-serif', fontWeight: '600', color: '#131A1A'}}>
              {studentInfo?.student_id || 'STU001'}
            </div>
            <div style={{color: '#798AA3', fontSize: 16, fontFamily: 'Outfit, sans-serif', fontWeight: '400'}}>
              Student ID
            </div>
          </div>

          {/* Personal Information Section */}
          <div style={{marginBottom: '24px', borderRadius: 12, borderLeft: '8px solid #F04915', overflow: 'hidden', background: 'white', boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)'}}>
            <div style={{background: '#F04915', padding: '16px 24px'}}>
              <h3 style={{fontSize: 20, fontFamily: 'Outfit, sans-serif', fontWeight: '600', color: 'white', margin: 0}}>
                Personal Information
              </h3>
            </div>
            <div style={{padding: '24px'}}>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px'}}>
                <InfoBlock label="Full Name" value={studentInfo?.full_name || studentInfo?.name || 'N/A'} />
                <InfoBlock label="Student ID" value={studentInfo?.student_id || 'N/A'} />
                <InfoBlock label="Email Address" value={studentInfo?.email || 'N/A'} />
                <InfoBlock label="Contact Number" value={studentInfo?.contact_no || studentInfo?.phone || 'N/A'} />
              </div>
            </div>
          </div>

          {/* Academic Information Section */}
          <div style={{marginBottom: '24px', borderRadius: 12, borderLeft: '8px solid #F04915', overflow: 'hidden', background: 'white', boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)'}}>
            <div style={{background: '#F04915', padding: '16px 24px'}}>
              <h3 style={{fontSize: 20, fontFamily: 'Outfit, sans-serif', fontWeight: '600', color: 'white', margin: 0}}>
                Academic Information
              </h3>
            </div>
            <div style={{padding: '24px'}}>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px'}}>
                <InfoBlock label="Program" value={studentInfo?.degree_programme || studentInfo?.program || 'N/A'} />
                <InfoBlock label="Year of Study" value={studentInfo?.year_of_study || 'N/A'} />
                <InfoBlock label="Department" value={studentInfo?.department || 'N/A'} />
                <InfoBlock label="Enrollment Date" value={studentInfo?.enrollment_date ? new Date(studentInfo.enrollment_date).toLocaleDateString() : 'N/A'} />
              </div>
            </div>
          </div>

          {/* Additional Academic Information Section */}
          <div style={{marginBottom: '40px', borderRadius: 12, borderLeft: '8px solid #F04915', overflow: 'hidden', background: 'white', boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)'}}>
            <div style={{background: '#F04915', padding: '16px 24px'}}>
              <h3 style={{fontSize: 20, fontFamily: 'Outfit, sans-serif', fontWeight: '600', color: 'white', margin: 0}}>
                Additional Information
              </h3>
            </div>
            <div style={{padding: '24px'}}>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px'}}>
                <InfoBlock label="Degree Path" value={studentInfo?.degree_path || 'N/A'} />
                <InfoBlock label="Group Number" value={studentInfo?.group_no || 'N/A'} />
                <InfoBlock label="Gender" value={studentInfo?.gender || 'N/A'} />
                <InfoBlock label="Date of Birth" value={studentInfo?.dob ? new Date(studentInfo.dob).toLocaleDateString() : 'N/A'} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper component for info blocks
const InfoBlock = ({ label, value }) => (
  <div style={{marginBottom: '16px'}}>
    <div style={{color: '#798AA3', fontSize: 14, fontFamily: 'Outfit, sans-serif', fontWeight: '500', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
      {label}
    </div>
    <div style={{color: '#131A1A', fontSize: 16, fontFamily: 'Outfit, sans-serif', fontWeight: '500', wordBreak: 'break-word'}}>
      {value || 'N/A'}
    </div>
  </div>
);

export default StudentProfile;
